/* eslint-disable react/no-array-index-key */
import { useTranslation } from 'react-i18next';
import {
  useCallback, useMemo, useRef, useEffect,
} from 'react';
import { useParams } from 'react-router';
import {
  getConversationItemProps,
} from '../../conversation-groups.util';
import STRING_KEYS from '../../../../../../../language/keys';
import { ConversationMinimizedResponseDto } from '../../../../../../../store/rtk-query/apis/outreach/dto/response/conversation-minimized.response';
import { ConversationItemType } from '../../../conversation-item/conversation-item.enums';
import Search from '../../../search/search.componet';
import ConversationItemSkeleton from '../../conversation-items-skeleton/conversation-items-skeleton.component';
import Spinner from '../../../../../../../components/Common/spinner/spinner.component';
import { SpinnerContainer } from '../conversation-group.styled';
import { ConversationsItemsProps } from './conversation-items';
import {
  ConversationItemsContainer,
  GroupContainer,
  GroupTitle,
  ScrollWrapper,
  StyleItem,
  TitleWrapper,
} from './conversation-items.styled';

type NestedItemsGroupsType = {
  tasks: ConversationMinimizedResponseDto[];
  scheduled: ConversationMinimizedResponseDto[];
  other: ConversationMinimizedResponseDto[];
};

const getDefaultNestedItemsGroups = () => ({
  tasks: [] as ConversationMinimizedResponseDto[],
  scheduled: [] as ConversationMinimizedResponseDto[],
  other: [] as ConversationMinimizedResponseDto[],
});

const ConversationItems = ({
  selectedConversationGroupName, conversations, isLoading, isFetching, loadMore, isAutoMode,
}: ConversationsItemsProps): JSX.Element | null => {
  const { t: translate } = useTranslation();
  const { positionId } = useParams<MatchUrlParams>();
  const lastItemRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (!lastItemRef.current) {
      return undefined;
    }
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new window.IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadMore(selectedConversationGroupName);
      }
    }, { threshold: 1.0 });
    observerRef.current.observe(lastItemRef.current);
    
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [conversations, selectedConversationGroupName, loadMore]);

  const nestedConversationsByGroup = useMemo<NestedItemsGroupsType>(() => {
    return conversations.reduce((acc, conversation) => {
      if (conversation) {
        if (conversation?.nextOpenLinkedinTask) {
          acc.tasks.push(conversation);
        } else if (conversation?.nextScheduledMessage) {
          acc.scheduled.push(conversation);
        } else {
          acc.other.push(conversation);
        }
      }
      
      return acc;
    }, getDefaultNestedItemsGroups()) || getDefaultNestedItemsGroups();
  }, [conversations]);

  const renderConversationItems = useCallback((activeConversations: ConversationMinimizedResponseDto[]) => {
    return activeConversations.map((conversation, index) => {
      if (conversation) {
        const {
          type,
          url,
          fullName,
          conversationMessage,
        } = getConversationItemProps(conversation, true);

        let sequenceStep;
        if (type === ConversationItemType.Scheduled) {
          sequenceStep = conversation.nextScheduledMessage?.sequenceStep;
        } else if (type === ConversationItemType.Task) {
          sequenceStep = conversation.nextOpenLinkedinTask?.sequenceStep;
        } else if (type === ConversationItemType.Default) {
          sequenceStep = conversation.sequenceLength ? conversation.sequenceLength + 1 : undefined;
        }

        const isLastItem = index === activeConversations.length - 1;
        
        return (
          <div
            ref={ isLastItem ? lastItemRef : undefined }
            key={ `wrapper_${conversation.id}_${index}` }
          >
            <StyleItem
              positionId={ positionId }
              key={ `${conversation.id}_${index}` }
              conversationId={ conversation.id }
              type={ type }
              url={ url }
              fullName={ fullName }
              conversationMessage={ conversationMessage }
              isAutoMode={ isAutoMode }
              totalSteps={ conversation.sequenceLength }
              completedSteps={ sequenceStep ? sequenceStep - 1 : undefined }
            />
          </div>
        );
      }
      
      return undefined;
    });
  }, [positionId, isAutoMode]);

  return (
    <ConversationItemsContainer>
      <TitleWrapper>
        <Search title={
          translate(STRING_KEYS.OUTREACH_PAGE.CONVERSATIONS_GROUP.NESTED_PANEL.TITLE,
            { number: conversations.length || 0 },
          )
        }
        />
      </TitleWrapper>
      { isLoading && (
        <ConversationItemSkeleton />
      )}
      {
        !isLoading && (
          <ScrollWrapper>
            {!!(nestedConversationsByGroup.tasks.length) && (
              <GroupContainer>
                <GroupTitle>{ translate(STRING_KEYS.OUTREACH_PAGE.CONVERSATIONS_GROUP.NESTED_PANEL.OPEN_TASKS_TITLE) }</GroupTitle>
                { renderConversationItems(nestedConversationsByGroup.tasks) }
              </GroupContainer>
            )}
            {!!(nestedConversationsByGroup.scheduled.length + nestedConversationsByGroup.other.length) && (
              <GroupContainer>
                <GroupTitle>{ translate(STRING_KEYS.OUTREACH_PAGE.CONVERSATIONS_GROUP.NESTED_PANEL.CONVERSATIONS_TITLE) }</GroupTitle>
                { renderConversationItems(nestedConversationsByGroup.scheduled) }
                { renderConversationItems(nestedConversationsByGroup.other) }
              </GroupContainer>
            )}
            { isFetching && (
              <SpinnerContainer>
                <Spinner width={ 20 } height={ 20 } animationDuration={ 1000 } />
              </SpinnerContainer>
            )}
          </ScrollWrapper>
        )
      }
    </ConversationItemsContainer>
  );
};

export default ConversationItems;
